import {
  isWorkbookErrorCell,
  type WorkbookDefinition,
  type WorkbookColumnarSheet,
  type WorkbookTable,
} from '@erp/shared-types';
import type { SheetPlan, WorkbookPlan } from '@erp/workbooks';
import { hasColumnFormula } from './worksheetColumns';
import {
  workbookItemIconKind,
  type WorkbookItemIconKind,
} from './workbookItemIconKind';

/** Where a Field a Formula reads comes from, which its Reference chip shows. */
export type FormulaFieldOrigin =
  | { kind: 'input'; inputName: string; inputIcon: WorkbookItemIconKind }
  | { kind: 'lookup'; sourceName: string; sourceIcon?: WorkbookItemIconKind }
  | { kind: 'calculated' };

export interface FormulaField {
  name: string;
  origin: FormulaFieldOrigin;
  /** A preview value, shown in the picker. */
  sample?: string;
}

/** Another Source or Worksheet, whose columns a lookup's Table references read. */
export interface FormulaTable {
  id: string;
  name: string;
  icon: WorkbookItemIconKind;
  /** Its columns, when known: empty for a Source not read yet. */
  columns: readonly string[];
}

/** The Fields one Formula may reference, by origin. */
export interface FormulaFields {
  /** The Worksheet's input, which pasted Qualified references may name. */
  inputName: string;
  /** The icon of the Worksheet's input: Excel for a file Source. */
  inputIcon: WorkbookItemIconKind;
  fields: readonly FormulaField[];
  /**
   * False while the input's columns are unknown: an unmatched name is then
   * presumed to be an input column rather than shown as unknown.
   */
  complete: boolean;
  /** The other tables a lookup (XLOOKUP, VLOOKUP) may read. */
  tables?: readonly FormulaTable[];
}

/**
 * Every Source and Worksheet but `sheetId`, which a lookup of its own
 * Worksheet would make a circular reference, with the columns the Plan knows
 * (none without a Plan).
 */
export function formulaTables(
  definition: Pick<WorkbookDefinition, 'files' | 'sources' | 'sheets'>,
  plan: Pick<WorkbookPlan, 'tables'> | undefined,
  sheetId: string,
): FormulaTable[] {
  return [...definition.sources, ...definition.sheets]
    .filter((item) => item.id !== sheetId)
    .map((item) => ({
      id: item.id,
      name: item.name,
      icon: workbookItemIconKind(definition, item.id) ?? 'data',
      columns: plan?.tables.get(item.id)?.map((field) => field.name) ?? [],
    }));
}

/** The table a Table reference names by id, or undefined when it names none. */
export function resolveFormulaTable(
  fields: FormulaFields,
  id: string,
): FormulaTable | undefined {
  return fields.tables?.find((table) => table.id === id);
}

/** Other tables' columns whose name or table name contains `query`, ignoring case. */
export function matchingTableColumns(
  fields: FormulaFields,
  query: string,
): { table: FormulaTable; column: string }[] {
  const needle = query.trim().toLowerCase();
  return (fields.tables ?? []).flatMap((table) =>
    table.columns
      .filter(
        (column) =>
          column.toLowerCase().includes(needle) ||
          table.name.toLowerCase().includes(needle),
      )
      .map((column) => ({ table, column })),
  );
}

function sampleValue(input: WorkbookTable | undefined, column: string) {
  const index = input?.columns.indexOf(column) ?? -1;
  if (!input || index < 0) return undefined;
  const value = input.rows
    .map((row) => row[index])
    .find((cell) => cell !== null && cell !== undefined && cell !== '');
  if (value === undefined || value === null) return undefined;
  return isWorkbookErrorCell(value) ? value.error : String(value);
}

/**
 * The Fields visible to the Formula of the column at `columnIndex` (or of a new
 * column), as the planner sees them: input columns, then Lookup outputs, then
 * the other calculated columns, a later origin shadowing an earlier one. A
 * Formula reading its own column's name reads the input value, so its own
 * column is not offered.
 */
export function worksheetFormulaFields({
  sheet,
  sheetPlan,
  inputName,
  inputIcon,
  input,
  itemName,
  itemIcon,
  columnIndex,
  tables,
}: {
  sheet: WorkbookColumnarSheet;
  sheetPlan?: SheetPlan;
  inputName: string;
  inputIcon: WorkbookItemIconKind;
  input?: WorkbookTable;
  itemName: (id: string) => string;
  /** The icon of a Lookup's source, so a Lookup reading an Excel file shows it. */
  itemIcon?: (id: string) => WorkbookItemIconKind | undefined;
  columnIndex?: number;
  /** The other tables a lookup may read (see `formulaTables`). */
  tables?: readonly FormulaTable[];
}): FormulaFields {
  const inputColumns =
    sheetPlan?.stages?.input.map((field) => field.name) ?? input?.columns;
  const byName = new Map<string, FormulaField>();
  for (const name of inputColumns ?? [])
    byName.set(name, {
      name,
      origin: { kind: 'input', inputName, inputIcon },
      sample: sampleValue(input, name),
    });
  for (const lookup of sheet.lookups) {
    byName.delete(lookup.outputColumn);
    byName.set(lookup.outputColumn, {
      name: lookup.outputColumn,
      origin: {
        kind: 'lookup',
        sourceName: itemName(lookup.sourceId),
        sourceIcon: itemIcon?.(lookup.sourceId),
      },
    });
  }
  sheet.columns.forEach((column, index) => {
    if (index === columnIndex || !hasColumnFormula(column)) return;
    byName.delete(column.name);
    byName.set(column.name, {
      name: column.name,
      origin: { kind: 'calculated' },
    });
  });
  return {
    inputName,
    inputIcon,
    fields: [...byName.values()],
    complete: inputColumns !== undefined,
    tables,
  };
}

/** The Field a reference names, or undefined when it names none. */
export function resolveFormulaField(
  fields: FormulaFields,
  name: string,
): FormulaField | undefined {
  const field = fields.fields.find((item) => item.name === name);
  if (field || fields.complete) return field;
  return {
    name,
    origin: {
      kind: 'input',
      inputName: fields.inputName,
      inputIcon: fields.inputIcon,
    },
  };
}

/** Fields whose name contains `query`, ignoring case, grouped in origin order. */
export function matchingFormulaFields(
  fields: FormulaFields,
  query: string,
): FormulaField[] {
  const needle = query.trim().toLowerCase();
  const order = { input: 0, lookup: 1, calculated: 2 };
  return fields.fields
    .filter((field) => field.name.toLowerCase().includes(needle))
    .toSorted((a, b) => order[a.origin.kind] - order[b.origin.kind]);
}
